import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
	type FunctionComponent,
	type ReactNode,
} from 'react'
import TagManager from 'react-gtm-module'
import { isDefined } from '../../utils/isDefined'

export const googleTagManagerId = process.env.NEXT_PUBLIC_GOOGLE_TAG_MANAGER_ID ?? ''
export const isTrackingEnabled = googleTagManagerId !== ''

const technicalCookiesString = 'technical'
const analyticalCookiesString = 'analytical'
const marketingCookiesString = 'marketing,personalization,userData'

const localStorageKey = 'granule-pod-lupou-cookies'
const cookiesList = [technicalCookiesString, analyticalCookiesString, marketingCookiesString] as const

type Value = {
	isBarVisible: boolean
	isAccepted: boolean
	isResetVisible: boolean
	forceOpen: () => void
	accept: () => void
	decline: () => void
	reset: () => void
	handleNonNecessaryCookies: (isMarketing?: boolean, isAnalytical?: boolean) => void
}

const cookieContext = createContext<Value>({
	isBarVisible: true,
	isResetVisible: false,
	isAccepted: false,
	forceOpen: () => {},
	accept: () => {},
	decline: () => {},
	reset: () => {},
	handleNonNecessaryCookies: () => {},
})

export type CookieProviderProps = {
	children: ReactNode
}

export const CookieProvider: FunctionComponent<CookieProviderProps> = ({ children }) => {
	const [isForcedOpen, setIsForcedOpen] = useState<boolean>(false)
	const [isAcceptedOrNotSet, setIsAcceptedOrNotSet] = useState<boolean | null>(null)
	useEffect(() => {
		const storageState = localStorage.getItem(localStorageKey)
		if (storageState !== null) {
			setIsAcceptedOrNotSet(localStorage.getItem(localStorageKey) ? true : false)
		}
	}, [isAcceptedOrNotSet])

	const cookies = useCallback(() => {
		const acceptedCookies = localStorage.getItem(localStorageKey)
		const isAcceptedCookies = acceptedCookies !== null

		return { acceptedCookies, isAcceptedCookies }
	}, [])

	const getUnnecessaryCookies = useCallback(() => {
		const { acceptedCookies } = cookies()
		const unnecessaryCookies = {
			isAnalyticalCookies: acceptedCookies?.includes(cookiesList[1]),
			isMarketingCookies: acceptedCookies?.includes(cookiesList[2]),
		}

		return unnecessaryCookies
	}, [cookies])

	const updateCookiesGTM = useCallback(() => {
		const { isAcceptedCookies } = cookies()

		const DataLayerCookies = {
			event: `cookie_consent_update`,
			ActiveGroups: [
				technicalCookiesString,
				getUnnecessaryCookies().isMarketingCookies ? marketingCookiesString : null,
				getUnnecessaryCookies().isAnalyticalCookies ? analyticalCookiesString : null,
			]
				.filter(isDefined)
				.join(','),
		}

		if (isTrackingEnabled) {
			TagManager.initialize({
				gtmId: googleTagManagerId,
				dataLayer: isAcceptedCookies ? DataLayerCookies : undefined,
			})
		}
	}, [cookies, getUnnecessaryCookies])

	const handleConsentChange = useCallback(
		(accepted: boolean | null) => {
			const necessaryCookies = cookiesList
				.map((value) => (value === technicalCookiesString ? value : null))
				.filter((value) => value !== null)
				.join(',')

			if (accepted === null) {
				localStorage.setItem(localStorageKey, necessaryCookies)
				updateCookiesGTM()
			} else {
				localStorage.setItem(
					localStorageKey,
					accepted ? cookiesList.map((value) => `${value}`).join(',') : necessaryCookies,
				)
				updateCookiesGTM()
			}
		},
		[updateCookiesGTM],
	)

	const [isVisibleAfterDelay, setIsVisibleAfterDelay] = useState(false)
	useEffect(() => {
		setTimeout(() => {
			setIsVisibleAfterDelay(true)
		}, 1000)
	}, [])

	const forceOpen = useCallback(() => {
		setIsForcedOpen(true)
	}, [])
	const accept = useCallback(() => {
		setIsForcedOpen(false)
		setIsAcceptedOrNotSet(true)
		handleConsentChange(true)
	}, [handleConsentChange])
	const decline = useCallback(() => {
		setIsForcedOpen(false)
		setIsAcceptedOrNotSet(false)
		handleConsentChange(false)
	}, [handleConsentChange])
	const reset = useCallback(() => {
		setIsForcedOpen(false)
		setIsAcceptedOrNotSet(null)
		handleConsentChange(null)
	}, [handleConsentChange])
	const handleNonNecessaryCookies = useCallback(
		(isMarketing?: boolean, isAnalytical?: boolean) => {
			setIsForcedOpen(false)
			setIsAcceptedOrNotSet(true)
			localStorage.setItem(
				localStorageKey,
				`${technicalCookiesString}${isAnalytical ? `,${analyticalCookiesString}` : ''}${isMarketing ? `,${marketingCookiesString}` : ''}`,
			)

			updateCookiesGTM()
		},
		[updateCookiesGTM],
	)

	const [isAccepted, setIsAccepted] = useState(false)
	useEffect(() => {
		if (isAcceptedOrNotSet === null) {
			return
		}
		setIsAccepted(isAcceptedOrNotSet)
	}, [isAcceptedOrNotSet])

	const value = useMemo(() => {
		const isUiVisible = isVisibleAfterDelay && isTrackingEnabled
		return {
			isBarVisible: (isAcceptedOrNotSet === null || isForcedOpen) && isUiVisible,
			isResetVisible: isAcceptedOrNotSet !== null && isUiVisible,
			forceOpen,
			accept,
			decline,
			reset,
			handleNonNecessaryCookies: handleNonNecessaryCookies,
			isAccepted: isAccepted,
		}
	}, [
		isVisibleAfterDelay,
		isAcceptedOrNotSet,
		isForcedOpen,
		forceOpen,
		accept,
		decline,
		reset,
		handleNonNecessaryCookies,
		isAccepted,
	])

	useEffect(() => {
		const initDataLayerCookies = {
			event: 'cookie_consent_update',
			ActiveGroups: [
				technicalCookiesString,
				getUnnecessaryCookies().isMarketingCookies ? marketingCookiesString : null,
				getUnnecessaryCookies().isAnalyticalCookies ? analyticalCookiesString : null,
			]
				.filter(isDefined)
				.join(','),
		}

		if (isTrackingEnabled) {
			TagManager.initialize({
				gtmId: googleTagManagerId,
				dataLayer: initDataLayerCookies,
			})
		}
	}, [cookies, getUnnecessaryCookies])

	return <cookieContext.Provider value={value}>{children}</cookieContext.Provider>
}

export const useIsCookieBarVisible = () => useContext(cookieContext).isBarVisible
export const useIsCookieResetVisible = () => useContext(cookieContext).isResetVisible
export const useIsCookiesAccepted = () => useContext(cookieContext).isAccepted
export const useForceOpenCookieBar = () => useContext(cookieContext).forceOpen
export const useAcceptCookies = () => useContext(cookieContext).accept
export const useDeclineCookies = () => useContext(cookieContext).decline
export const useResetCookies = () => useContext(cookieContext).reset
export const useHandleNonNecessaryCookies = () => useContext(cookieContext).handleNonNecessaryCookies
